import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  XCircle,
} from 'lucide-react';

import {
  CustomerSales,
  CustomerSalesSection as CustomerSalesData,
  PaceRow,
  PaceStatus,
} from '../../services/quoteToCashTypes';

import { compactMoney, formatDate, fullMoney, percent } from './format';

type SortKey = 'target' | 'sales' | 'lowest' | 'highest';

const SORTS: Record<SortKey, (a: PaceRow, b: PaceRow) => number> = {
  target: (a, b) => b.annualTarget - a.annualTarget || b.ytdSales - a.ytdSales,
  sales: (a, b) => b.ytdSales - a.ytdSales,
  // Rows without a target have no ratio: they go after the others
  lowest: (a, b) =>
    (a.performanceRatio ?? Infinity) - (b.performanceRatio ?? Infinity) ||
    b.ytdSales - a.ytdSales,
  highest: (a, b) =>
    (b.performanceRatio ?? -Infinity) - (a.performanceRatio ?? -Infinity) ||
    b.ytdSales - a.ytdSales,
};

const STATUS: Record<
  PaceStatus,
  { icon: React.ElementType; fill: string; text: string }
> = {
  green: { icon: CheckCircle2, fill: 'bg-green-600', text: 'text-green-700' },
  yellow: { icon: AlertTriangle, fill: 'bg-amber-500', text: 'text-amber-700' },
  red: { icon: XCircle, fill: 'bg-red-600', text: 'text-red-700' },
};

// Name, progress bar, YTD sales, target, pace
const COLUMNS =
  'md:grid md:grid-cols-[minmax(0,2.2fr)_minmax(0,1.6fr)_minmax(0,0.9fr)_minmax(0,0.9fr)_minmax(0,1.1fr)] md:items-center md:gap-4';

/** A customer's amounts in CAD at today's rate, so customers in different currencies sort together */
const inCAD = (customer: CustomerSales): CustomerSales => ({
  ...customer,
  annualTarget: customer.annualTarget * (customer.cadRate ?? 1),
  ytdSales: customer.ytdSales * (customer.cadRate ?? 1),
});

/**
 * Section 3: YTD sales by customer, expandable to ship-to, against targets at the year's pace, in each customer's
 * currency (that of its UD04 targets)
 */
export const CustomerSalesSection: React.FC<{ data: CustomerSalesData }> = ({
  data,
}) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [sort, setSort] = useState<SortKey>('target');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const customers = useMemo(
    () =>
      [...data.customers]
        .sort((a, b) => SORTS[sort](inCAD(a), inCAD(b)))
        .map((c) => ({ ...c, shipTos: [...c.shipTos].sort(SORTS[sort]) })),
    [data.customers, sort]
  );

  const toggle = (custId: string) =>
    setExpanded((open) => {
      const next = new Set(open);
      if (next.has(custId)) next.delete(custId);
      else next.add(custId);
      return next;
    });

  const row = (
    item: PaceRow,
    key: string,
    level: 'customer' | 'shipTo',
    currency: string,
    expander?: React.ReactNode
  ) => {
    const status = STATUS[item.status];
    const Icon = status.icon;
    const hasTarget = item.annualTarget > 0;
    const bar = hasTarget ? (
      <div
        className="relative h-2.5 rounded-full bg-gray-200"
        role="img"
        aria-label={t('quoteToCash.customers.barLabel', {
          done: percent(item.completion ?? 0, lang, 0),
          elapsed: percent(data.fiscalYearProgress, lang, 0),
        })}
      >
        <div
          className={`h-full rounded-full ${status.fill}`}
          style={{ width: `${(item.completion ?? 0) * 100}%` }}
        />
        <div
          className="absolute -top-1 -bottom-1 w-0.5 bg-gray-900"
          style={{ left: `${data.fiscalYearProgress * 100}%` }}
          title={t('quoteToCash.today')}
        />
      </div>
    ) : (
      <span className="text-gray-400">—</span>
    );
    return (
      <div
        key={key}
        className={`${COLUMNS} py-2.5 px-2 border-b border-gray-100 text-sm ${level === 'shipTo' ? 'bg-gray-50' : ''}`}
      >
        <div
          className={`flex items-center gap-1.5 min-w-0 ${level === 'shipTo' ? 'pl-7' : ''}`}
        >
          {expander}
          <span
            className={`truncate ${level === 'customer' ? 'font-medium text-gray-900' : 'text-gray-700'}`}
            title={item.name}
          >
            {item.name}
          </span>
        </div>
        <div className="mt-2 md:mt-0">{bar}</div>
        <div className="flex gap-4 mt-1 md:contents">
          <span
            className="tabular-nums text-gray-900 md:text-right"
            title={fullMoney(item.ytdSales, lang, currency)}
          >
            {compactMoney(item.ytdSales, lang, currency)}
          </span>
          <span
            className="tabular-nums text-gray-600 md:text-right"
            title={
              hasTarget
                ? fullMoney(item.annualTarget, lang, currency)
                : undefined
            }
          >
            {compactMoney(item.annualTarget, lang, currency)}
          </span>
          <span className={`inline-flex items-center gap-1.5 ${status.text}`}>
            <Icon className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
            <span className="tabular-nums">
              {item.performanceRatio === null
                ? t('quoteToCash.customers.noTarget')
                : t('quoteToCash.customers.ofPace', {
                    pct: percent(item.performanceRatio, lang, 0),
                  })}
            </span>
          </span>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <p className="text-sm text-gray-600">
          {t('quoteToCash.customers.legend', {
            pct: percent(data.fiscalYearProgress, lang, 0),
            date: formatDate(data.asOf, lang, 'long'),
          })}
        </p>
        <label className="inline-flex items-center gap-2 text-sm text-gray-700">
          {t('quoteToCash.customers.sortBy')}
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as SortKey)}
            className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm"
          >
            {(Object.keys(SORTS) as SortKey[]).map((key) => (
              <option key={key} value={key}>
                {t(`quoteToCash.customers.sort.${key}`)}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div
        className={`hidden ${COLUMNS} px-2 pb-2 border-b border-gray-200 text-xs font-medium uppercase tracking-wide text-gray-500`}
      >
        <span className="pl-6">{t('quoteToCash.customers.name')}</span>
        <span>{t('quoteToCash.customers.progress')}</span>
        <span className="text-right">{t('quoteToCash.customers.ytd')}</span>
        <span className="text-right">{t('quoteToCash.customers.target')}</span>
        <span>{t('quoteToCash.customers.pace')}</span>
      </div>
      {customers.map((customer) => {
        const open = expanded.has(customer.custId);
        const Chevron = open ? ChevronDown : ChevronRight;
        return (
          <React.Fragment key={customer.custId}>
            {row(
              customer,
              customer.custId,
              'customer',
              customer.currencyCode,
              <button
                type="button"
                onClick={() => toggle(customer.custId)}
                aria-expanded={open}
                aria-label={t('quoteToCash.customers.showShipTos', {
                  name: customer.name,
                })}
                disabled={customer.shipTos.length === 0}
                className="p-0.5 rounded text-gray-500 hover:bg-gray-100 disabled:invisible"
              >
                <Chevron className="w-4 h-4" />
              </button>
            )}
            {open &&
              customer.shipTos.map((shipTo) =>
                row(
                  shipTo,
                  `${customer.custId}/${shipTo.shipToNum}`,
                  'shipTo',
                  customer.currencyCode
                )
              )}
          </React.Fragment>
        );
      })}
    </>
  );
};
